import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfirmService, HttpLoaderService, NotificationCommunicationService } from '@tymes4-shared';
import { Component, Inject, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { AssigmentPolicyInvitesSettingEnum, TicketView, TicketService, TicketUpdatePolicyArgs, TicketPolicyUpdate, AssigmentPolicyUnpaidOrderSettingEnum, LongRunningOperationTypeEnum, AssignmentPolicyC3VSettingEnum, ConfigService } from '../../api';
import { AssignmentPolicyValidator } from '../edit-assignment-policy/assignment-policy.validator';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { SnackbarHelper } from '../../helpers/snackbar-helper';

@Component({
    selector: 'app-edit-event-placement-policy',
    templateUrl: 'edit-event-placement-policy.component.html',
    styleUrls: ['./edit-event-placement-policy.component.scss'],
    standalone: false
})
export class EditEventPlacementPolicyComponent implements OnInit {
    public form: FormGroup;
    public saving: boolean = false;
    public isDirty: boolean = false;
    public longRunningOperationTypeEnum = LongRunningOperationTypeEnum;
    public AssignmentPolicyC3VSettingEnum = AssignmentPolicyC3VSettingEnum;

    public tickets: Array<TicketView> = [];

    validationMessages = {
        'RevokeTimeSpan': [
            { type: 'invalidTimeSpan', message: 'time span invalid' },
        ]
    };

    public assignmentEnum = AssigmentPolicyInvitesSettingEnum;
    public unpaidAssignmentEnum = AssigmentPolicyUnpaidOrderSettingEnum;
    public C3VIsEnabled: boolean;

    @ViewChild('timeSpanControl') timeSpanControl;

    constructor(
        @Inject(MAT_DIALOG_DATA) public passedData: any,
        public dialogRef: MatDialogRef<EditEventPlacementPolicyComponent>,
        private loader: HttpLoaderService,
        private ticketService: TicketService,
        private confirmService: ConfirmService,
        private snackbar: SnackbarHelper,
        private notificationCommunicationService: NotificationCommunicationService,
        private translate: TranslateService,
        private configService: ConfigService) { }

    ngOnInit(): void {
        this.tickets = this.passedData.tickets;
        this.form = new FormGroup({
            RevokeTimeSpan: new FormControl({ value: null, disabled: this.assignmentsDisabled() }, { validators: AssignmentPolicyValidator.ValidateTimeSpan })
        });

        this.configService.c3vIsEnabled().subscribe((C3VIsEnabled: boolean) => {
            this.C3VIsEnabled = C3VIsEnabled;
        });
    }

    toggleAssignmentsActive(toggle: boolean) {
        if (!toggle) {
            this.setValueForEveryTicket('AssignmentRequired', toggle);
            this.toggleAssignmentRequired(toggle);
        }

        this.timeSpanControl.setDisabledState(!toggle);
    }

    toggleAssignmentRequired(toggle: boolean) {
        if (!toggle) {
            this.setValueForEveryTicket('IdentificationRequired', toggle);
            this.setValueForEveryTicket('BlacklistCheck', toggle);
            this.setValueForEveryTicket('EnforcePurchasingRights', toggle);
            this.setValueForEveryTicket('CheckAge', toggle);
            this.setValueForEveryTicket('AllowUnpaidPositions', toggle);
            this.setValueForEveryTicket('AllowViewTicketsOfLinkedCustomers', toggle);
            this.setValueForEveryTicket('IdentificationReferenceDate', null);
            this.setValueForEveryTicket('RestrictCompanyAssignments', toggle)
        }
    }

    updateRadioValue(ev: any, field: string) {
        this.isDirty = true;
        this.tickets.forEach((t) => {
            t[field] = ev.target.value as AssigmentPolicyInvitesSettingEnum;
        });
    }

    updateBooleanValue(ev: any, field: string) {
        this.isDirty = true;
        this.setValueForEveryTicket(field, ev.target.checked);

        if (field === 'AssignmentsActive') {
            this.toggleAssignmentsActive(ev.target.checked);
        } else if (field === 'AssignmentRequired') {
            this.toggleAssignmentRequired(ev.target.checked);
        } else if (field === 'IdentificationRequired' && !ev.target.checked) {
            this.setValueForEveryTicket('IdentificationReferenceDate', null);
        }
    }

    updateDateValue(ev: any, field: string) {
        this.isDirty = true;
        this.setValueForEveryTicket(field, ev?.toDate());
    }

    isIndeterminate(field: string) {
        const firstTicket = this.tickets[0];
        return !this.tickets.every(t => t[field] === firstTicket[field]);
    }

    assignmentsDisabled() {
        return this.tickets.every(t => t['AssignmentsActive'] === false);
    }

    isDisabled() {
        return this.tickets.every(t => t['AssignmentRequired'] === false);
    }

    getShownValue(field: string) {
        const firstTicket = this.tickets[0];
        if (this.tickets.every(t => t[field] === firstTicket[field])) {
            if (field === 'IdentificationReferenceDate') {
                const refDate = firstTicket[field];
                return refDate != null ? moment(refDate).format('DD-MM-YYYY') : null;
            }

            return firstTicket[field];
        }

        return null;
    }

    revokedTimeSpanChanged() {
        this.isDirty = true;
    }

    setValueForEveryTicket(field: string, value: any) {
        this.tickets.forEach((t) => {
            t[field] = value;
        });
    }

    submit() {
        this.saving = true;
        const ticketForm = this.form.getRawValue();
      const updates: Array<TicketPolicyUpdate> = [];
      this.tickets.forEach((ticket) => {
            updates.push({
                EventPlacementId: ticket.EventPlacementId,
                AssignmentsActive: ticket.AssignmentsActive,
                AssignmentRequired: ticket.AssignmentRequired,
                IdentificationRequired: ticket.IdentificationRequired,
                BlacklistCheck: ticket.BlacklistCheck,
                C3VBlacklistCheck: this.C3VIsEnabled ? ticket.C3VBlacklistCheck : AssignmentPolicyC3VSettingEnum.A,
                EnforcePurchasingRights: ticket.EnforcePurchasingRights,
                CheckAge: ticket.CheckAge,
                AllowingInvites: ticket.AllowingInvites,
                AllowUnpaidPositions: ticket.AllowUnpaidPositions,
                AllowUnpaidPositionsType: ticket.AllowUnpaidPositionsType,
                AllowUnpaidTicketPositions: ticket.AllowUnpaidTicketPositions,
                AllowViewTicketsOfLinkedCustomers: ticket.AllowViewTicketsOfLinkedCustomers,
                RevokeTimeSpan: ticketForm.RevokeTimeSpan ?? ticket.RevokeTimeSpan,
                IdentificationReferenceDate: ticket.IdentificationReferenceDate,
                RestrictCompanyAssignments: ticket.RestrictCompanyAssignments,
            })
        });

        const args: TicketUpdatePolicyArgs = {
            UpdateRows: updates
        }

      if (updates.length <= 1) {
        this.loader.open();
        this.ticketService.updateTicketAssignmentPolicies(args).subscribe(() => {
          this.loader.close();
          this.saving = false;
          this.dialogRef.close(true);
        }, (error) => {
          this.loader.close();
          this.saving = false;
          this.confirmService.confirm({
            okonly: true,
            title: this.translate.instant('DIALOGS.EDIT-EVENT-PLACEMENT-POLICY-FORM.CONFIRM.TITLE.ERROR'),
            message: this.translate.instant('DIALOGS.EDIT-EVENT-PLACEMENT-POLICY-FORM.CONFIRM.MESSAGE.ERROR'),
          }).subscribe();
        });

        return;
      }

      this.confirmService.confirm({
        okonly: false,
        title: this.translate.instant('DIALOGS.EDIT-EVENT-PLACEMENT-POLICY-FORM.CONFIRM.TITLE.WARNING'),
        message: this.translate.instant('DIALOGS.EDIT-EVENT-PLACEMENT-POLICY-FORM.CONFIRM.MESSAGE.WARNING', {ticketAmmount: updates.length})
      }).subscribe((ok: boolean) => {
        if (!ok) {
          this.saving = false;
          return;
        }

        this.loader.open();
        this.ticketService.updateTicketAssignmentPolicies(args).subscribe({
          next: (result) => {
            this.snackbar.open('GENERIC.SNACKBAR.UPDATE-TICKETS');
            this.notificationCommunicationService.updateNotificationsAndLROS();
            this.loader.close();
            this.saving = false;
            this.dialogRef.close(false);
          },
          error: (err) => {
            this.confirmService.confirm({
              okonly: true,
              title: this.translate.instant('DIALOGS.EDIT-EVENT-PLACEMENT-POLICY-FORM.CONFIRM.TITLE.ERROR'),
              message: this.translate.instant('DIALOGS.EDIT-EVENT-PLACEMENT-POLICY-FORM.CONFIRM.MESSAGE.ERROR'),
            }).subscribe();
            this.loader.close();
            this.saving = false;
          },
        });
      });
    }
}
